import { ampli } from '@/ampli';
import type { WalletClient } from 'viem';

import { ClientInputError, ClientTransactionError, ClientWalletError } from '@/lib/errors/client';
import { logger } from '@/lib/logger';

import { fillOrder } from '@/components/TransactionModule/fillOrder';
import { getFailureReason } from '@/components/TransactionModule/MarketMode/SubmitTrade/utils';
import { trackRwaSwapInitiated } from '@/components/TransactionModule/utils';

import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import type { Price, Quote, SwapQuote } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import type { EthereumAddress, EthereumHash } from '@/utils/models';
import { logReferralToSpindl } from '@/utils/referrals';
import { routes } from '@/utils/routes';
import { getSubmissionReason } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import {
  getPrivyWalletClient,
  isUserRejection,
  reportWalletGuardDiagnostics,
} from '@/utils/wallet';

import type { FailureEvent } from '../analytics';
import { buildTokenEventFields, matchaReferrer, slippageFromQuote } from '../analytics';
import type {
  ExecutionContext,
  PreflightResult,
  SubmitOutcomeId,
  SubmitResult,
  TradeStrategy,
} from '../types';

const WALLET_CONNECTION_ERROR_MESSAGE =
  'Wallet connection error. Please try again or reconnect your wallet.';

type ExecutableSwapQuote = SwapQuote & { taker: EthereumAddress };

/** Quote snapshot captured by preflight for the standard and batch EVM strategies. */
export type PreparedEvmTrade = PreflightResult & {
  raw: Price | Quote | undefined;
};

type EvmStrategyOptions = {
  lane: 'evm' | 'batch';
  transactionType: 'swap' | 'batch-swap';
  send(args: {
    ctx: ExecutionContext;
    quote: ExecutableSwapQuote;
    client: WalletClient;
  }): Promise<SubmitOutcomeId>;
};

function buildMarketFailureEvent(
  ctx: ExecutionContext,
  raw: Price | Quote | undefined,
  details: Pick<FailureEvent, 'failureStage' | 'failureReason' | 'submissionReason'>,
): FailureEvent {
  const slippage = raw ? slippageFromQuote(raw) : { bps: undefined, mode: undefined };
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    swapOrderType: 'market',
    swapExecutionType: 'standard',
    swapSlippageBps: slippage.bps,
    swapSlippageMode: slippage.mode,
    matchaReferrer: matchaReferrer(ctx),
    recipientAddress: ctx.recipientAddress,
    ...details,
  };
}

function postTradeSubmit(ctx: ExecutionContext, quote: ExecutableSwapQuote, hash: string) {
  if (ctx.referrer.id) {
    if (ctx.referrer.isExpired()) {
      ctx.referrer.clear();
    } else if (ctx.accountAddress) {
      void logReferralToSpindl({
        quote,
        txnHash: hash,
        sellTokenDecimals: ctx.sellToken.decimals,
        buyTokenDecimals: ctx.buyToken.decimals,
        tokenUsdPrices: ctx.tokenUsdPrices,
        address: ctx.accountAddress,
        chainId: ctx.chainId.toString(),
        referrer: ctx.referrer.id,
        isGasless: false,
        status: 'pending',
      });
    }
  }
  trackRwaSwapInitiated({
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    orderType: 'market',
    transactionHash: hash,
  });
  if (ctx.accountAddress) {
    void fetch(routes.api.TRADE_SUBMIT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress: ctx.accountAddress,
        tokenAddress: ctx.buyToken.address,
        chainId: ctx.chainId,
      }),
    })
      .then((response) => {
        if (!response.ok) logger.warn('[Trade Submit] API error:', response.status);
      })
      .catch((error: unknown) => {
        logger.warn('[Trade Submit] Failed to log recommendation trade:', error);
      });
  }
  ctx.deps.resetInputs();
}

/** Runs one EVM market submit end-to-end: guards, send, record, analytics, and cleanup. */
async function submitEvmTrade(
  options: EvmStrategyOptions,
  ctx: ExecutionContext,
  prepared: PreparedEvmTrade,
): Promise<SubmitResult> {
  ampli.swapInitiated({
    ...buildTokenEventFields({
      source: 'input',
      sellToken: ctx.sellToken,
      buyToken: ctx.buyToken,
      sellInput: ctx.sellInput,
      buyInput: ctx.buyInput,
    }),
    matcha_referrer: matchaReferrer(ctx),
    recipient_address: ctx.recipientAddress,
    swap_order_type: 'market',
    swap_execution_type: 'standard',
  });

  // Every failure, including a user rejection, invalidates quotes so the form reprices.
  const fail = async (rejected: boolean, error: Error): Promise<SubmitResult> => {
    await ctx.deps.invalidateTradeQuotes();
    return { success: false, rejected, error };
  };

  const chain = SUPPORTED_CHAINS_BY_CHAIN_ID[ctx.chainId];
  const client = chain ? await getPrivyWalletClient(ctx.ethereumWallet, chain) : undefined;

  if (!ctx.accountAddress) {
    const error = new ClientWalletError(WALLET_CONNECTION_ERROR_MESSAGE);
    trackError(error);
    return fail(false, error);
  }
  if (!client) {
    reportWalletGuardDiagnostics({
      ethereumWallet: ctx.ethereumWallet,
      targetChainId: ctx.sellToken.chainId,
      accountAddress: ctx.accountAddress,
      walletClientSource: 'privy_direct',
    });
    trackTransactionFailed(
      buildMarketFailureEvent(ctx, prepared.raw, {
        failureStage: 'pre_submission',
        failureReason: 'wallet_not_ready',
      }),
    );
    const error = new ClientWalletError(WALLET_CONNECTION_ERROR_MESSAGE);
    trackError(error);
    return fail(false, error);
  }
  if (!prepared.raw) {
    const error = new ClientInputError('cant submit swap order: no quote');
    trackError(error);
    return fail(false, error);
  }

  try {
    if (!('transaction' in prepared.raw) || !('taker' in prepared.raw)) {
      throw new ClientTransactionError('No transaction data provided!');
    }
    if (prepared.raw.taker !== ctx.accountAddress) throw new Error('taker mismatch');
    const quote: ExecutableSwapQuote = prepared.raw;

    const outcome = await options.send({ ctx, quote, client });

    const tokenFields = buildTokenEventFields({
      source: 'quote',
      sellToken: ctx.sellToken,
      buyToken: ctx.buyToken,
      sellAmount: quote.sellAmount,
      buyAmount: quote.buyAmount,
      tokenUsdPrices: ctx.tokenUsdPrices,
    });
    const slippage = slippageFromQuote(quote);
    ctx.deps.addTransaction(
      {
        sellToken: ctx.sellToken,
        sellAmount: quote.sellAmount,
        sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
        sellAmountUsd: tokenFields.sell_token_amount_usd,
        buyToken: ctx.buyToken,
        buyAmount: quote.buyAmount,
        buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
        buyAmountUsd: tokenFields.buy_token_amount_usd,
        feeSource: quote.fees.zeroExFee,
        referrerId: matchaReferrer(ctx),
        // The batch lane stores its EIP-5792 callsId here; the legacy record shape and the watchers expect it in the hash field.
        hash: outcome.value as EthereumHash,
        timestamp: new Date().toISOString(),
        transactionType: options.transactionType,
        recipient: ctx.recipientAddress,
        slippageBps: slippage.bps,
        slippageMode: slippage.mode,
      },
      ctx.accountAddress,
      ctx.chainId,
    );
    ampli.transactionSubmitted({
      ...tokenFields,
      is_smart_wallet: ctx.isSmartWallet,
      matcha_referrer: matchaReferrer(ctx),
      recipient_address: ctx.recipientAddress,
      swap_order_type: 'market',
      swap_execution_type: 'standard',
      swap_slippage_bps: slippage.bps,
      swap_slippage_mode: slippage.mode,
      swap_tx_hash: outcome.value,
    });
    postTradeSubmit(ctx, quote, outcome.value);
    return { success: true, outcome };
  } catch (error: unknown) {
    const rejected = isUserRejection(error);
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    trackTransactionFailed(
      buildMarketFailureEvent(ctx, prepared.raw, {
        failureStage: 'submission',
        failureReason: rejected ? 'user_rejected' : getFailureReason(normalizedError),
        submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
      }),
    );
    if (!rejected) trackError(error);
    return fail(rejected, normalizedError);
  }
}

/** Creates the two EVM market strategies that differ only in send primitive and record type. */
export function createEvmStrategy(options: EvmStrategyOptions): TradeStrategy<PreparedEvmTrade> {
  return {
    lane: options.lane,
    preflight: (ctx) =>
      ctx.quote.source === 'swap' && ctx.ethereumWallet
        ? { chainFamily: 'evm', raw: ctx.quote.raw }
        : null,
    submit: (ctx, prepared) => submitEvmTrade(options, ctx, prepared),
  };
}

/** Standard EOA or smart-wallet EVM swap strategy. */
export const evmStrategy = createEvmStrategy({
  lane: 'evm',
  transactionType: 'swap',
  send: async ({ ctx, quote, client }) => ({
    kind: 'txHash',
    value: await fillOrder({
      transaction: quote.transaction,
      chainId: ctx.chainId,
      walletClient: client,
      connectorId: ctx.ethereumWallet?.meta.id,
    }),
  }),
});
